/**
 * refreshSession — the one place that calls POST /auth/refresh.
 *
 * Shared by BOTH axios clients (`api_instance` for the analytics app,
 * `portalClient` for the admin portal) because refresh tokens are SINGLE-USE:
 * confirmed live, refreshing returns a new refresh_token and the old one then
 * gets 401 {"detail":"Invalid refresh token"}. If each client refreshed on its
 * own, two near-simultaneous 401s (one per client) would both spend the same
 * refresh token — the second call fails and forces a logout. Funnelling every
 * refresh through one in-flight promise means the token is spent exactly once
 * and every caller gets the same new access token.
 *
 * On success this persists the new pair to cookies and notifies the Zustand
 * store (via the `auth:token-refreshed` event — importing the store here would
 * be a circular dependency). On failure it only rejects: what "session over"
 * means differs per client (which login page to go to), so callers handle it.
 */

import axios from "axios";
import { z } from "zod";

import { tokenStorage } from "./tokenStorage";
import { API_ENDPOINTS } from "../constants/api";
// Type-only: the store imports telemetry, which leads back to api_instance →
// this file, so a runtime import here would be circular.
import type { AuthTokens } from "../store/authStore";

const BASE_URL = process.env.REACT_APP_BASE_URL || "/api";

// Shape observed from the live API (same TokenResponse as /auth/login and
// /admin/auth/login): {access_token, refresh_token, token_type: "bearer",
// expires_in: 7200, refresh_token_expires_in: 604800}. Parsed rather than
// trusted so a contract change fails loudly here instead of writing
// `undefined` into the cookies.
const RefreshResponseSchema = z.object({
  access_token: z.string(),
  refresh_token: z.string(),
  expires_in: z.number(),
  refresh_token_expires_in: z.number(),
});

let inFlight: Promise<string> | null = null;

async function doRefresh(): Promise<string> {
  const refreshToken = tokenStorage.getRefreshToken();
  if (!refreshToken) throw new Error("No refresh token");

  // A plain axios call (not either client) so their 401 interceptors can't
  // loop back into another refresh. `baseURL` in the config, NOT
  // string-concatenated into the url: a hand-built `${BASE_URL}${url}` breaks
  // when BASE_URL is a bare "/" — "/" + "/auth/refresh" = "//auth/refresh",
  // which the browser reads as a protocol-relative URL to a host named
  // "auth" (net::ERR_NAME_NOT_RESOLVED). axios's own joining collapses that.
  const { data } = await axios.post(
    API_ENDPOINTS.AUTH.REFRESH,
    { refresh_token: refreshToken },
    {
      baseURL: BASE_URL,
      headers: {
        "Content-Type": "application/json",
        ClientDb: process.env.REACT_APP_CLIENT_DB || "",
      },
    },
  );
  const tokens = RefreshResponseSchema.parse(data);

  // Persist with the API-supplied lifetimes so the cookies expire with the tokens.
  tokenStorage.setTokens(
    tokens.access_token,
    tokens.refresh_token,
    tokens.expires_in,
    tokens.refresh_token_expires_in,
  );

  const detail: AuthTokens = {
    accessToken: tokens.access_token,
    refreshToken: tokens.refresh_token,
    expiresIn: tokens.expires_in,
    refreshTokenExpiresIn: tokens.refresh_token_expires_in,
  };
  window.dispatchEvent(new CustomEvent("auth:token-refreshed", { detail }));

  return tokens.access_token;
}

/** Refresh the session, joining any refresh already in flight. Resolves to the new access token. */
export function refreshSession(): Promise<string> {
  if (!inFlight) {
    inFlight = doRefresh().finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
}
